import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import Img from '@/components/shared/Img';

const reviews = [
  {
    name: 'Gustavo Berlanga',
    text: 'Terza Costa came out as soon as I called. Everything was done exactly as explained and completed on time.',
  },
  {
    name: 'Emalee Loudermilk',
    text: "I called multiple contractors in a bind and Terza Costa was so much help. Professional and quick — I would recommend them to anyone.",
  },
  {
    name: 'Kaitlyn Witsaman',
    text: "The Terza Costa team truly transformed our master bathroom. Communication was on point, they worked within our budget, and came in with a competitive bid we couldn't refuse. We are already dreaming about our next project with them.",
  },
  {
    name: 'Richard Weld',
    text: 'We chose Justin and his team to do a minor floor repair. It soon turned out to be a much larger project because of the hidden damage. Justin was outstanding in his communication throughout — from detailed photos of what was happening to negotiating materials on our behalf. The finished product was exactly what I had hoped for and more. I highly recommend including Justin in bidding for any repair work you may be considering.',
  },
  {
    name: 'Sherman Myles',
    text: 'Great professional service with very reasonable prices. The workmanship was clean and precise. They showed up on time, worked efficiently, and left everything spotless. I am extremely satisfied with the quality and would absolutely use them again.',
  },
  {
    name: 'Yvette Ruiz',
    text: "We couldn't be happier with our kitchen remodel! The entire process was smooth and professional. The team listened carefully to what we wanted and the final design was even better than we imagined. If you're considering a kitchen remodel, I highly recommend Terza Costa Construction!",
  },
];

export default function ReviewsSection() {
  return (
    <section className="relative py-20 md:py-28 bg-[#111111]">
      <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-[#0a0a0a] to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <div className="w-12 h-px bg-[#C97B2E] mx-auto mb-6" />
          <span className="font-body text-[#C97B2E] text-xs font-bold uppercase tracking-[0.3em] mb-4 block">
            Testimonials
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
            What Our Clients Say
          </h2>
          <p className="font-body text-white/50 text-base max-w-xl mx-auto mb-6">
            Real reviews from real homeowners across the Coastal Bend.
          </p>
          <Link
            to="/leave-a-review"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/15 hover:border-[#C97B2E]/50 text-white/70 hover:text-white px-5 py-2.5 rounded-sm font-body font-bold uppercase tracking-wider text-xs transition-all duration-300"
          >
            <Star className="w-4 h-4 text-[#C97B2E] fill-[#C97B2E]" />
            Leave Us a Review
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((review, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="bg-[#1a1a1a] border border-white/10 rounded-sm p-8 relative group hover:border-[#C97B2E]/40 hover:shadow-lg transition-all duration-500"
            >
              {/* Orange accent top bar */}
              <div className="absolute top-0 left-8 w-12 h-0.5 bg-[#C97B2E]" />

              <Quote className="w-8 h-8 text-white/10 mb-4" aria-hidden="true" />

              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-[#C97B2E] fill-[#C97B2E]" />
                ))}
              </div>

              <p className="font-body text-white/70 text-sm leading-relaxed mb-6 italic">
                "{review.text}"
              </p>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C97B2E]/15 border border-[#C97B2E]/30 flex items-center justify-center">
                  <span className="font-heading text-[#C97B2E] font-bold text-sm">
                    {review.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <span className="font-body text-white font-bold text-sm block">{review.name}</span>
                  <span className="font-body text-white/60 text-xs">Verified Client · Google Review</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Featured Testimonial Spotlight */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-sm overflow-hidden border border-white/10"
        >
          {/* Photo */}
          <div className="lg:col-span-2 relative">
            <Img
              src="/images/33cad0df0_630532657_1669404440725600_8366413664056088405_n.jpg"
              alt="Emergency water heater replacement completed by Terza Costa Construction — Rockport TX"
              className="w-full h-64 lg:h-full object-cover"
              sizes="(min-width: 1024px) 40vw, 100vw"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-4 left-4">
              <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#0a0a0a] bg-[#C97B2E] px-3 py-1 rounded-sm">
                Emergency water heater replacement
              </span>
            </div>
          </div>

          {/* Quote */}
          <div className="lg:col-span-3 bg-[#1a1a1a] border-l border-white/10 p-10 md:p-12 flex flex-col justify-center relative">
            <div className="absolute top-0 left-0 w-16 h-0.5 bg-[#C97B2E]" />
            <Quote className="w-10 h-10 text-white/10 mb-6" aria-hidden="true" />
            <div className="flex gap-1 mb-5">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="w-4 h-4 text-[#C97B2E] fill-[#C97B2E]" />
              ))}
            </div>
            <blockquote className="font-heading text-white text-xl md:text-2xl italic leading-relaxed mb-8">
              "We moved in and found out we had no hot water for a full week. Justin had zero hesitation coming over after hours to help us in an emergency. Terza Costa got our new water heater installed quickly, and we were so grateful for the fast response and willingness to help."
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C97B2E]/15 border border-[#C97B2E]/30 flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-[#C97B2E] font-bold text-sm">EL</span>
              </div>
              <div>
                <p className="font-body text-white font-bold text-sm">Emalee Loudermilk</p>
                <p className="font-body text-white/60 text-xs">Verified Client · Rockport, TX</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}